import { listTasksQuerySchema, taskIdSchema } from '@quizdb/common';
import { TaskListFilters } from './taskEndpoints';

/**
 * The filters' schema, without the cursor, which pages rather than filters (QZ-D8). Built from
 * `listTasksQuerySchema` directly: it carries no refinement, so `.omit` is safe on it.
 */
const filterSchema = listTasksQuerySchema.omit({ cursor: true });

const FILTER_KEYS = [
  'status',
  'tag',
  'taskType',
  'difficulty',
  'ageGroup',
  'language',
  'jobId',
  'document',
  'section',
] as const;

/**
 * Drops `document` and `section` from `filters` unless `status` is `approved` (QZ-D105), and
 * drops `section` without a `document`. Shared by `readFilters`, for the URL, and
 * `readTaskNavState`, for filters carried in router state, so neither can send the API a
 * document or section filter the status doesn't allow.
 */
export const applyDocumentFilterRule = (filters: TaskListFilters): TaskListFilters => {
  const document = filters.status === 'approved' ? filters.document : undefined;
  return { ...filters, document, section: document !== undefined ? filters.section : undefined };
};

/**
 * Reads the task list's filters from the URL query string, dropping any unknown or invalid
 * value field by field rather than failing the whole parse (QZ-D8). A missing `status` becomes
 * `draft`, the schema's default. `applyDocumentFilterRule` then applies the approved-only rule
 * (QZ-D105), so a shared or stale URL such as `?document=Atlas` under a `draft` status never
 * reaches the API, which would otherwise refuse it with `400`.
 */
export const readFilters = (searchParams: URLSearchParams): TaskListFilters => {
  const candidate: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value !== null && filterSchema.shape[key].safeParse(value).success) {
      candidate[key] = value;
    }
  }
  return applyDocumentFilterRule(filterSchema.parse(candidate));
};

/** The URL query string for `filters`, omitting "any" values and the default status (QZ-D8). */
export const writeFilters = (filters: TaskListFilters): URLSearchParams => {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value === undefined || (key === 'status' && value === 'draft')) {
      continue;
    }
    params.set(key, value);
  }
  return params;
};

/** The task list's path for `filters`, such as `/tasks?status=approved&jobId=…`. */
export const taskListPath = (filters: TaskListFilters): string => {
  const query = writeFilters(filters).toString();
  return query ? `/tasks?${query}` : '/tasks';
};

/** A status change just made, shown on the next page with a link back to the task. */
export interface TaskNotice {
  outcome: 'approved' | 'rejected';
  taskId: string;
  question: string;
}

/**
 * What travels in router state between the task list and the task page: the list's filters, so
 * the next draft comes from the same list, and the notice of the last status change.
 */
export interface TaskNavState {
  from?: TaskListFilters;
  notice?: TaskNotice;
}

const readNotice = (value: unknown): TaskNotice | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const { outcome, taskId, question } = value as Record<string, unknown>;
  return (outcome === 'approved' || outcome === 'rejected') &&
    taskIdSchema.safeParse(taskId).success &&
    typeof question === 'string'
    ? { outcome, taskId: taskId as string, question }
    : undefined;
};

/**
 * Router state is whatever the history holds, so it is checked rather than trusted. `from` also
 * gets `applyDocumentFilterRule` (QZ-D105), since router state can carry a filter set from
 * before a status change — e.g. reopening an approved task under a `document` filter and then
 * moving on to the next draft — and the rule is what keeps that draft lookup from being refused.
 */
export const readTaskNavState = (state: unknown): TaskNavState => {
  if (typeof state !== 'object' || state === null) {
    return {};
  }
  const { from, notice } = state as Record<string, unknown>;
  const filters = filterSchema.safeParse(from);
  const parsedNotice = readNotice(notice);
  return {
    ...(from !== undefined && filters.success
      ? { from: applyDocumentFilterRule(filters.data) }
      : {}),
    ...(parsedNotice ? { notice: parsedNotice } : {}),
  };
};
